import { useEffect, useState } from 'react';
import { useAppSelector } from 'src/store';
import { useNavigate } from 'react-router-dom';
import {
  useGetAllProjectsQuery,
  useGetKnowledgeBaseQuery,
  useLazyGetCollectorsStatusQuery,
  useRunKnowledgeBaseCollectorMutation,
} from 'src/services';
import { Button } from 'src/components/ui/button';
import { ProductsType } from 'src/constants/products';
import { PATH_ONBOARDING } from 'src/constants/spa-routes';
import { AddItemIcon } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep2/AddItemIcon/AddItemIcon';
import { CheckIcon } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep2/CheckIcon/CheckIcon';
import { UseExistingKBAccordion } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep2/UseExistingKBAccordion/UseExistingKBAccordion';
import { CreateNewKBAccordion } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep2/CreateNewKBAccordion/CreateNewKBAccordion';
import { Separator } from 'src/components/ui/separator';
import { API_VERSION } from 'src/constants/api-routes';
import type { KnowledgeBaseCollector } from 'src/services/knowledgeBaseService';
import { getApiVersion } from 'src/utils/apiVersion';

const OnboardingStep2 = () => {
  const { project, knowledgeBase } = useAppSelector((s) => s.onboardingSlice);
  const navigate = useNavigate();
  const { data: projects } = useGetAllProjectsQuery();
  const [knowledgeBases, setKnowledgeBases] = useState(null);
  const [assignedKBs, setAssignedKBs] = useState(null);
  const [collectorsArr, setCollectorsArr] = useState<KnowledgeBaseCollector[]>([]);
  const [nextButtonDisabled, setNextButtonDisabled] = useState(true);
  const currentProject = project.id ? projects?.find((item) => item.id === +project.id) : null;
  const { data: knowledgeBaseData } = useGetKnowledgeBaseQuery(
    {
      load_projects: true,
      page_size: 100,
    },
    { refetchOnMountOrArgChange: true, skip: !currentProject?.id },
  );

  const [runKnowledgeBaseCollector] = useRunKnowledgeBaseCollectorMutation();
  const [fetchCollectorsStatus] = useLazyGetCollectorsStatusQuery();

  useEffect(() => {
    if (projects && !currentProject) {
      navigate(`${PATH_ONBOARDING.ROOT}/1?type=${ProductsType.customer}`, { replace: true });
    }
  }, [projects, currentProject]);

  useEffect(() => {
    if (knowledgeBaseData) {
      const kbs = knowledgeBaseData.items.filter((kb) => {
        return kb.id !== knowledgeBase?.id;
      });

      const assignedKbs = kbs.filter((kb) => {
        return kb.projects.some((project) => project.id === currentProject?.id);
      });

      setAssignedKBs(assignedKbs);
      setKnowledgeBases(kbs);
    }
  }, [knowledgeBase, knowledgeBaseData]);

  const back = () => {
    navigate(`${PATH_ONBOARDING.ROOT}/1?type=${ProductsType.customer}`, { replace: true });
  };

  const next = async () => {
    const collectors = [...collectorsArr];
    const promises = [];
    const version = getApiVersion();
    setNextButtonDisabled(true);

    if (collectors.length > 0) {
      for (const collector of collectors) {
        if (version === API_VERSION.V1 && collector) {
          const { data } = await fetchCollectorsStatus({ collectorId: collector.id });

          if (['idle', 'failed'].includes(data.status)) {
            const promise = runKnowledgeBaseCollector({
              knowledgeBaseId: knowledgeBase.id,
              collectorId: collector.id,
            });

            promises.push(promise);
          }
        }

        if (
          version === API_VERSION.V2 &&
          collector &&
          ['zendesk_tickets', 'zendesk_helpcenter', 'freshdesk_tickets', 'web_scrapper'].includes(
            collector.type,
          )
        ) {
          const promise = runKnowledgeBaseCollector({
            knowledgeBaseId: knowledgeBase.id,
            collectorId: collector.id,
          });

          promises.push(promise);
        }
      }
    }

    await Promise.all(promises);
    setNextButtonDisabled(false);
    navigate(`${PATH_ONBOARDING.ROOT}/3?type=${ProductsType.customer}`, { replace: true });
  };

  const onCollectorsChanged = (collectors: KnowledgeBaseCollector[]) => {
    setCollectorsArr(collectors);
  };

  const onDisabledChange = (disabledNewKB: boolean) => {
    setNextButtonDisabled(disabledNewKB);
  };

  return (
    <div className={'w-full max-w-[650px] h-[60%] max-h-[60%]'}>
      {knowledgeBases && Boolean(knowledgeBases.length) && (
        <div className={'flex flex-col w-full max-w-[455px] m-auto'}>
          <UseExistingKBAccordion project={currentProject} items={knowledgeBases} />
          <Separator className={'bg-[#E3E6E8] mt-5'} />
        </div>
      )}
      {knowledgeBases && (
        <div className={'flex flex-col w-full items-center'}>
          <CreateNewKBAccordion
            project={currentProject}
            item={knowledgeBase}
            knowledgeBases={knowledgeBases}
            onCollectorsChanged={onCollectorsChanged}
            onDisabledChange={onDisabledChange}
          />
        </div>
      )}
      <div className={'mt-5 flex items-center justify-center gap-5 px-[50px]'}>
        <div>
          <CheckIcon className={'invisible pointer-events-none'} />
        </div>
        <div className={'flex flex-col w-full items-center justify-center gap-5'}>
          <Button
            disabled={nextButtonDisabled && !assignedKBs?.length}
            className="w-full py-6 font-semibold"
            onClick={next}
          >
            <p>Continue</p>
          </Button>
          <button
            type="button"
            onClick={back}
            className={'text-[#3F60B3] text-xs font-medium cursor-pointer hover:underline'}
          >
            ← Back
          </button>
        </div>
        <div>
          <AddItemIcon className={'invisible pointer-events-none'} />
        </div>
      </div>
    </div>
  );
};

export default OnboardingStep2;
